import { V2Position, V2Vault } from '@/core/migrate';
import { MIGRATOR_ZAP } from '@/domain/migrate';
import {
  Address,
  Provider,
  Signer,
  TokenId,
  VAULT_FEE_TIER,
  Vault,
  WeiPerEther,
  decreaseByPercentage,
  fetchAllAssets,
  fetchVTokenToEth,
  getChainConstant,
  getContract,
  increaseByPercentage,
} from 'nftx.js';
import MigratorZap from '@/domain/migrate/abis/MigratorZap';
import { formatEther, parseEther } from 'viem';
import { calculateLiquidity } from '@/infrastructure/positions';

async function migrateSlp({
  fullRange,
  network,
  position,
  provider,
  signData,
  signer,
  v3Vault,
  vault,
  slippage,
}: {
  network: number;
  provider: Provider;
  signer: Signer;
  vault: Pick<V2Vault, 'lpStakingPool' | 'id' | 'asset' | 'is1155' | 'token'>;
  position: Pick<V2Position, 'migrateOptions' | 'slp'>;
  signData: { signature: string } | undefined;
  v3Vault: Pick<Vault, 'vaultId' | 'id'>;
  fullRange: boolean;
  slippage: number;
}) {
  const contract = getContract({
    address: getChainConstant(MIGRATOR_ZAP, network),
    abi: MigratorZap,
    provider,
    signer,
  });

  const sushiPair = vault.lpStakingPool.stakingToken.id;
  const migrateOption = position.migrateOptions.find(
    (o) => o.vaultId === v3Vault.vaultId
  );
  if (!migrateOption) {
    throw new Error('Invalid vaultId');
  }
  // We can only migrate whole units so we floor the vToken amount
  const maxVToken = migrateOption.maxVToken;
  const maxEth = migrateOption.maxEth;
  // Get a non-ether count of the whole units
  const vTokenCount = Math.floor(Number(formatEther(maxVToken)));
  // Add slippage to the slp amount - although if slippage adds more than the total amount then it will fail
  let slpToRemove = increaseByPercentage(migrateOption.maxSlp, slippage);
  if (slpToRemove > position.slp) {
    slpToRemove = position.slp;
  }
  const vTokenV2 = vault.token.id;

  // We want to make sure there are enough assets to redeem
  // Grab all redeemable assets owned by the v2 vault
  const holdings = await fetchAllAssets({
    network,
    userAddress: vault.id,
    assetAddress: vault.asset.id,
    vaultId: v3Vault.vaultId,
  });
  // Not enough assets to redeem
  if (holdings.length < vTokenCount) {
    throw new Error(
      `There are not enough eligible NFTs in the v2 vault to redeem ${vTokenCount} NFTs`
    );
  }
  const is1155 = vault.is1155;
  const permitSig = signData?.signature;
  const v3VaultId = v3Vault.vaultId;
  const vTokenToEth = await fetchVTokenToEth({
    provider,
    vaultAddress: v3Vault.id,
  });
  // The standard range is -90% to +100%
  const amountALower =
    vTokenToEth - (vTokenToEth * parseEther('0.9')) / WeiPerEther;
  const amountAUpper = vTokenToEth * 10n;

  // Leverage the LP logic to calculate the ticks and liquidity amounts
  const {
    tickLower,
    tickUpper,
    addLiqAmount0,
    addLiqAmount1,
    sqrtRatioCurrentX96,
  } = await calculateLiquidity({
    amountACurrent: vTokenToEth,
    amountALower,
    amountAUpper,
    feeTier: VAULT_FEE_TIER,
    network,
    tokenB: v3Vault.id,
    // addLiqAmountB: vTokenWholeUnits,
    addLiqAmountA: maxEth,
    fullRange,
  });
  const deadline = BigInt(Math.floor(Date.now() / 1000) + 60 * 30);

  const amount0Min = decreaseByPercentage(addLiqAmount0, slippage);
  const amount1Min = decreaseByPercentage(addLiqAmount1, slippage);

  // Add slippage tolerance to the ETH amount but not the vToken (as we need the exact unit)
  // if (addLiqAmount0 !== vTokenWholeUnits) {
  //   amount0Min = decreaseByPercentage(addLiqAmount0, slippage);
  // } else {
  //   amount1Min = decreaseByPercentage(addLiqAmount1, slippage);
  // }

  return contract.write.sushiToNFTXAMM({
    args: [
      {
        amount0Min,
        amount1Min,
        deadline,
        fee: VAULT_FEE_TIER,
        is1155,
        // 0x is for smart wallets to bypass permit
        permitSig: (permitSig as Address) || '0x',
        lpAmount: slpToRemove,
        sushiPair,
        tickLower,
        sqrtPriceX96: sqrtRatioCurrentX96,
        tickUpper,
        vaultIdV3: BigInt(v3VaultId),
        vTokenV2,
      },
    ],
  });
}

export default migrateSlp;
